// ./src/contexts/UsersContext.tsx
//
// Context wrapper around the useUsers hook.
//
// Purpose:
// - Allows sibling components (table, edit drawer, toolbar)
//   to share the same users state without prop-drilling.
// - Each page creates its own <UsersProvider> with its own config,
//   so UsersPage state doesn't leak into other pages.
// - The underlying useUsers hook remains the data engine; this context
//   simply makes its return value available to the component tree.
//
// Usage:
//
//   // In a page component:
//   <UsersProvider autoLoad={false} pageSize={20}>
//     <UsersPageContent />
//     <UserEditDrawer />
//   </UsersProvider>
//
//   // In any child component:
//   const { users, loading, update, ... } = useUsersContext();
//
//   // In a component that may or may not be inside a provider (e.g. drawer):
//   const context = useOptionalUsersContext();
//   const updateFn = context?.update ?? props.onUpdate;

import React, { createContext, useContext, useMemo } from "react";

import { useUsers, UseUsersOptions, UseUsersReturn } from "@/hooks/useUsers";

// ============================================
// Context
// ============================================

const UsersContext = createContext<UseUsersReturn | null>(null);

// ============================================
// Provider
// ============================================

export interface UsersProviderProps extends UseUsersOptions {
  children: React.ReactNode;
}

/**
 * Wraps useUsers into a context provider.
 *
 * Each instance creates its own independent useUsers store
 * with its own filters, pagination, loading state, and error state.
 *
 * Note: The provider value is defensively memoized so that even if
 * useUsers is refactored to return a non-memoized object in the
 * future, context consumers won't receive unnecessary re-renders.
 * Currently useUsers already wraps its return in useMemo, so this
 * is a safety net rather than a performance fix.
 *
 * @example
 * <UsersProvider autoLoad={false} pageSize={20} initialSort={[{ name: "username", order: "ASC" }]}>
 *   <MyPageContent />
 * </UsersProvider>
 */
export const UsersProvider: React.FC<UsersProviderProps> = ({
  children,
  ...options
}) => {
  const usersData = useUsers(options);

  // Defensive memo: re-create the context value only when the hook's
  // return object identity changes. Today useUsers already memoizes
  // its return, so `usersData` is stable when its deps are stable.
  // This guard protects against future refactors that might remove
  // the internal useMemo.
  const value = useMemo(() => usersData, [usersData]);

  return (
    <UsersContext.Provider value={value}>
      {children}
    </UsersContext.Provider>
  );
};

// ============================================
// Hooks
// ============================================

/**
 * Access the UsersContext. Throws if used outside a UsersProvider.
 *
 * Use this in components that are always rendered inside a provider
 * (e.g. UsersList, UsersToolbar).
 */
export const useUsersContext = (): UseUsersReturn => {
  const context = useContext(UsersContext);

  if (!context) {
    throw new Error(
      "useUsersContext must be used within a <UsersProvider>. " +
      "Wrap your component tree with <UsersProvider> or use useOptionalUsersContext() instead."
    );
  }

  return context;
};

/**
 * Access the UsersContext if available, or return null.
 *
 * Use this in components that may or may not be inside a provider
 * (e.g. UserEditDrawer which could work both inside UsersPage's provider
 * and standalone elsewhere).
 *
 * @example
 * const context = useOptionalUsersContext();
 * const updateFn = context?.update ?? props.onUpdate;
 * const loadFn = context?.get ?? props.onLoad;
 */
export const useOptionalUsersContext = (): UseUsersReturn | null => {
  return useContext(UsersContext);
};